/**
 * Sección "Cuestionarios" de /projects/$id/evaluations-config.
 *
 * Lista los cuestionarios nivel `project` (templates) con su estado de
 * aplicación al proyecto. Lead/admin puede activar/desactivar opt-in.
 *
 * Para el detalle de cada cuestionario (preguntas, respuestas, etc) se va
 * a /evaluations/$id desde el componente EvaluationsCard.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { ClipboardList, EyeOff, Lock, Power, PowerOff, Repeat } from "lucide-react";
import {
  EVAL_RECURRENCE_LABELS,
  EVAL_VISIBILITY_LABELS,
  type ProjectActivation,
  type Questionnaire,
  type SessionUser,
} from "@axorial/shared";
import { api } from "@/lib/api";
import { canSeeDef } from "@/lib/eval-visibility";

interface TemplateRow {
  id: string;
  name: string;
  level: Questionnaire["level"];
  visibility: Questionnaire["visibility"];
  recurrence: Questionnaire["recurrence"];
  active: boolean;
  isAnonymous: boolean;
  appliesToProjects: Questionnaire["appliesToProjects"];
  description: string | null;
}

export function ProjectQuestionnairesConfigSection({ projectId }: { projectId: string }) {
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const templatesQ = useQuery<{ templates: TemplateRow[] }>({
    queryKey: ["questionnaires", "all"],
    queryFn: () => api.get("/questionnaires?includeInactive=1"),
  });
  const activationsQ = useQuery<{ activations: ProjectActivation[] }>({
    queryKey: ["project", projectId, "activations"],
    queryFn: () => api.get(`/projects/${projectId}/activations`),
  });

  const activationByDef = useMemo(() => {
    const m = new Map<string, ProjectActivation>();
    for (const a of activationsQ.data?.activations ?? []) {
      if (a.defKind === "questionnaire") m.set(a.defId, a);
    }
    return m;
  }, [activationsQ.data]);

  const buckets = useMemo(() => {
    if (!me.data || !templatesQ.data) {
      return {
        active: [] as TemplateRow[],
        available: [] as TemplateRow[],
        not_applicable: [] as TemplateRow[],
      };
    }
    const all = templatesQ.data.templates.filter((t) => t.active && t.level === "project");
    const active: TemplateRow[] = [];
    const available: TemplateRow[] = [];
    const not_applicable: TemplateRow[] = [];

    for (const t of all) {
      if (!canSeeDef(t.visibility, me.data.role)) continue;
      const act = activationByDef.get(t.id);
      if (t.appliesToProjects === "all") {
        if (act?.active === false) available.push(t);
        else active.push(t);
      } else if (t.appliesToProjects === "per_project_opt_in") {
        if (act?.active === true) active.push(t);
        else available.push(t);
      } else {
        not_applicable.push(t);
      }
    }
    return { active, available, not_applicable };
  }, [me.data, templatesQ.data, activationByDef]);

  const canEdit = me.data?.role === "admin" || me.data?.role === "lead";
  const isLoading = templatesQ.isLoading || activationsQ.isLoading;

  if (isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <span className="loading loading-spinner loading-md text-primary" />
      </div>
    );
  }

  const total = buckets.active.length + buckets.available.length + buckets.not_applicable.length;
  if (total === 0) {
    return (
      <div className="card-elevated py-16 text-center">
        <ClipboardList className="size-10 text-primary/40 mx-auto mb-3" />
        <p className="text-sm text-base-content/60">
          No hay cuestionarios nivel proyecto. Pídele al admin que cree uno desde
          /admin/questionnaires.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Bucket
        title="Activos en este proyecto"
        subtitle="Cuestionarios disponibles para que el cliente o el equipo respondan en este proyecto."
        items={buckets.active}
        state="active"
        projectId={projectId}
        canEdit={canEdit}
      />
      <Bucket
        title="Disponibles — activar manualmente"
        items={buckets.available}
        state="available"
        projectId={projectId}
        canEdit={canEdit}
      />
      {buckets.not_applicable.length > 0 && (
        <Bucket
          title="No aplicables a proyectos"
          subtitle="Definidos con applies_to_projects='none'. Cambia la definición global desde /admin/questionnaires."
          items={buckets.not_applicable}
          state="locked"
          projectId={projectId}
          canEdit={false}
        />
      )}
    </div>
  );
}

function Bucket({
  title,
  subtitle,
  items,
  state,
  projectId,
  canEdit,
}: {
  title: string;
  subtitle?: string;
  items: TemplateRow[];
  state: "active" | "available" | "locked";
  projectId: string;
  canEdit: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <section className="space-y-2">
      <header>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        {subtitle && <p className="text-sm text-base-content/60">{subtitle}</p>}
      </header>
      <ul className="space-y-2">
        {items.map((t) => (
          <Row key={t.id} template={t} projectId={projectId} state={state} canEdit={canEdit} />
        ))}
      </ul>
    </section>
  );
}

function Row({
  template,
  projectId,
  state,
  canEdit,
}: {
  template: TemplateRow;
  projectId: string;
  state: "active" | "available" | "locked";
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const upsert = useMutation({
    mutationFn: (active: boolean) =>
      api.put(`/projects/${projectId}/activations`, {
        defKind: "questionnaire",
        defId: template.id,
        active,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId, "activations"] }),
  });

  return (
    <li className={`card-elevated p-4 ${state === "locked" ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{template.name}</span>
            {template.isAnonymous && (
              <span className="badge badge-warning badge-sm gap-1">
                <EyeOff className="size-3" /> anónimo
              </span>
            )}
            <span className="badge badge-ghost badge-sm gap-1">
              <Lock className="size-3" /> {EVAL_VISIBILITY_LABELS[template.visibility]}
            </span>
            <span className="badge badge-ghost badge-sm gap-1">
              <Repeat className="size-3" /> {EVAL_RECURRENCE_LABELS[template.recurrence]}
            </span>
          </div>
          {template.description && (
            <p className="text-xs text-base-content/55 mt-1">{template.description}</p>
          )}
        </div>
        {state === "active" && canEdit && (
          <button
            type="button"
            onClick={() => {
              if (confirm(`Desactivar "${template.name}" en este proyecto?`)) upsert.mutate(false);
            }}
            disabled={upsert.isPending}
            className="btn btn-ghost btn-sm gap-1"
          >
            <PowerOff className="size-3.5" /> Desactivar
          </button>
        )}
        {state === "available" && canEdit && (
          <button
            type="button"
            onClick={() => upsert.mutate(true)}
            disabled={upsert.isPending}
            className="btn btn-primary btn-sm gap-1"
          >
            {upsert.isPending && <span className="loading loading-spinner loading-xs" />}
            <Power className="size-3.5" /> Activar
          </button>
        )}
        {state === "locked" && (
          <span className="text-xs text-base-content/45 self-center">No aplicable</span>
        )}
      </div>
    </li>
  );
}
